export type TitleSource = 'auto' | 'user';

interface TitledSession {
  title: string;
  titleSource?: TitleSource;
}

/**
 * 会话标题是自动生成的，还是用户自己起的。
 *
 * 历史会话没有 titleSource 字段，缺省按 'user' 处理——只有机器产物的**确定签名**才判为
 * 'auto'。宁可少升级一个标题，也不能改掉用户起的名字。
 *
 * 原判据是 `title === 'New Project' || title.endsWith('...') || title.length <= 30`，
 * 第三条会把用户手改的短标题当成自动标题覆盖掉。触发路径：刷新页面后在已改名的会话中
 * 追加一条消息——autoGenerateSessionTitle 只在生成完成时被调用，而它依赖的
 * titleGeneratedRef 是纯内存 Set，刷新即清空，不提供任何跨刷新保护。
 *
 * 已知代价：由旧的自动改写产出的标题（不带 ... 且非 'New Project'）会被判为 'user'
 * 而冻结，不再升级为 projectName。可接受——它们本就是可读标题，冻结是安全的方向。
 */
export function resolveTitleSource(session: TitledSession): TitleSource {
  if (session.titleSource) return session.titleSource;
  if (session.title === 'New Project') return 'auto';
  if (session.title.endsWith('...')) return 'auto';
  return 'user';
}

export function shouldAutoRename(session: TitledSession): boolean {
  return resolveTitleSource(session) === 'auto';
}

interface AnalysedTurn {
  role: string;
  pipeline?: { requirement?: { projectName?: string } };
}

/**
 * What to rename a session to, or `undefined` to leave it as it is.
 *
 * Two things were wrong with doing this inline.
 *
 * It read the FIRST assistant turn. A session that went through a confirmation
 * card has a clarify turn there, and a clarify turn carries no pipeline — so
 * `projectName` was always empty and the good title was never reachable. The
 * analysis lands on whichever turn analysed, which is the one to read.
 *
 * And when it found nothing it wrote a 20-character slice of the prompt anyway.
 * That is worse than the title already there — `createInitialSessionTitle`
 * keeps 30 characters and ends them with `...` — and it also **froze the
 * session**: `applySessionTitle` marks `titleSource: 'auto'` in local state,
 * but the PATCH that follows sends only `{ title }`, and `conversation_sessions`
 * has no column for it. After a reload the session came back with a plain
 * 20-character title and no marker, which `resolveTitleSource` reads as a name
 * the user chose. So the placeholder permanently disqualified the session from
 * the upgrade it was waiting for. Writing nothing keeps that door open.
 */
export function resolveAutoSessionTitle(
  session: TitledSession & { messages?: AnalysedTurn[] },
): string | undefined {
  if (!shouldAutoRename(session)) return undefined;

  for (const message of session.messages || []) {
    if (message.role !== 'assistant') continue;
    const projectName = (message.pipeline?.requirement?.projectName || '').trim();
    if (projectName) return projectName;
  }

  return undefined;
}

